import React from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, ChatCircleDots, Compass, UserGear } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const CARDS = [
  {
    to: "/walkthrough?mode=quick",
    label: "Ask your Coach a question",
    icon: ChatCircleDots,
    title: "Ask your Coach a question.",
    body: "One focused exchange. Describe the moment, get a recommended move and the words to use.",
    testid: "start-card-ask",
  },
  {
    to: "/walkthrough?mode=guided",
    label: "Help me resolve a live conflict",
    icon: Compass,
    title: "Help me resolve a live conflict.",
    body: "Step-by-step coaching — calm, clarify, listen, build a path forward. Use it when you have time to think it all the way through.",
    testid: "start-card-guide",
  },
  {
    to: "/coach",
    label: "Contact your live coach",
    icon: UserGear,
    title: "Contact your live coach.",
    body: "Send a text or video message to your executive coach, or book a live call.",
    testid: "start-card-coach",
  },
];

export default function LeaderStart() {
  const { leader, coach } = useAuth();

  // Loading auth state
  if (leader === null && coach === null) {
    return (
      <div className="text-center text-[#8A948E] py-16">Loading…</div>
    );
  }
  // Coach lands somewhere else
  if (coach) return <Navigate to="/coach/dashboard" replace />;
  // Not signed in — bounce to login
  if (!leader) return <Navigate to="/auth/login" replace />;

  const firstName = (leader.name || leader.email || "").split(/[\s@]/)[0];

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-12 lg:py-20">
      <div className="anim-in delay-1">
        <div className="label-kicker" style={{ color: "#1E3A2B" }}>Welcome back</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1E3A2B] tracking-tight leading-tight mt-3">
          Having a difficult conversation? Get assistance now.
        </h1>
        {firstName && (
          <p className="text-[#4A524C] mt-3 leading-relaxed max-w-2xl">
            Hello, {firstName}. Pick what fits the moment.
          </p>
        )}
      </div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 anim-in delay-2">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <Link
              key={c.to}
              to={c.to}
              data-testid={c.testid}
              className="group bg-white border border-[#E8E4DB] rounded-2xl p-6 hover:border-[#1E3A2B] transition-all flex flex-col"
            >
              <span className="w-10 h-10 rounded-full bg-[#1E3A2B]/8 text-[#1E3A2B] flex items-center justify-center flex-shrink-0">
                <Icon size={20} weight="duotone" />
              </span>
              <h3 className="font-serif text-xl text-[#1E3A2B] mt-5 leading-snug">{c.title}</h3>
              <p className="text-[#4A524C] mt-2 leading-relaxed text-sm flex-1">{c.body}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#1E3A2B]">
                {c.label}
                <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
